import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import { DataGrid, type GridPaginationModel, type GridSortModel } from '@mui/x-data-grid';
import React, { useMemo, useRef, useState } from 'react';
import { AdminsPageShell } from '@/components/AdminsPageShell';
import { useDebounce } from '@/hooks/useDebounce';
import { useGetAssets } from '@/hooks/useGetAssets';
import { useGetCompanies } from '@/hooks/useGetCompanies';
import { useGetCountries } from '@/hooks/useGetCountries';
import type { Asset, Company, Country, MaDealType } from '@/types/GraphqlSchema';
import { TABLE_COLUMNS } from './constants';
import { useGetDeals } from './hooks/useGetDeals';
import { useGetDealTypes } from './hooks/useGetEventTypes';
import type { FilterItemOption, MaDealListFilterValue, MaDealsListFilters } from './types';

type FilterItemProps = {
  label: string;
  value: MaDealListFilterValue;
  onChange: (value: MaDealListFilterValue) => void;
  options: MaDealListFilterValue[];
  loading: boolean;
  inputValue?: string;
  onInputChange?: (event: React.SyntheticEvent<Element, Event>, value: string) => void;
};

// Generic filter item component for Autocomplete
const FilterItem = ({
  label,
  value,
  onChange,
  options,
  loading,
  inputValue = undefined,
  onInputChange = undefined,
}: FilterItemProps) => {
  return (
    <Autocomplete
      sx={{ flex: 1 }}
      autoComplete
      value={value}
      options={options}
      loading={loading}
      popupIcon={<ExpandMoreIcon />}
      getOptionLabel={option => {
        const filterItem = option as FilterItemOption;
        return filterItem.displayName || filterItem.name || '';
      }}
      onChange={(_, newValue) => onChange(newValue as MaDealListFilterValue)}
      noOptionsText='No entities found'
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      {...(inputValue !== undefined ? { inputValue } : {})}
      {...(onInputChange !== undefined ? { onInputChange } : {})}
      renderInput={params => (
        <TextField
          {...(params as React.ComponentProps<typeof TextField>)}
          label={label}
          variant='outlined'
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color='inherit' size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            },
          }}
        />
      )}
    />
  );
};

type FilterProps = {
  value: MaDealListFilterValue;
  onChange: (value: MaDealListFilterValue) => void;
};

const CountryFilter = ({ value, onChange }: FilterProps) => {
  const { countries, loading } = useGetCountries();
  return (
    <FilterItem
      label='Country'
      value={value}
      onChange={onChange}
      options={countries}
      loading={loading}
    />
  );
};

const BuyerFilter = ({ value, onChange }: FilterProps) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const { companies, loading } = useGetCompanies({
    variables: { search: debouncedSearch, maBuyers: true, includeHistoricalParticipants: true },
  });

  return (
    <FilterItem
      label='Buyer'
      value={value}
      onChange={onChange}
      options={companies}
      loading={loading}
      inputValue={search}
      onInputChange={(_, newInputValue) => setSearch(newInputValue)}
    />
  );
};

const SellerFilter = ({ value, onChange }: FilterProps) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const { companies, loading } = useGetCompanies({
    variables: { search: debouncedSearch, maSellers: true, includeHistoricalParticipants: true },
  });

  return (
    <FilterItem
      label='Seller'
      value={value}
      onChange={onChange}
      options={companies}
      loading={loading}
      inputValue={search}
      onInputChange={(_, newInputValue) => setSearch(newInputValue)}
    />
  );
};

const AssetFilter = ({ value, onChange }: FilterProps) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const { assets, loading } = useGetAssets({ search: debouncedSearch });

  return (
    <FilterItem
      label='Asset'
      value={value}
      onChange={onChange}
      options={assets}
      loading={loading}
      inputValue={search}
      onInputChange={(_, newInputValue) => setSearch(newInputValue)}
    />
  );
};

const EventTypeFilter = ({ value, onChange }: FilterProps) => {
  const { dealTypes, loading } = useGetDealTypes();

  return (
    <FilterItem
      label='Event Type'
      value={value}
      onChange={onChange}
      options={dealTypes}
      loading={loading}
    />
  );
};

type FiltersProps = {
  filters: MaDealsListFilters;
  setFilters: (filters: MaDealsListFilters) => void;
};

const Filters = ({ filters, setFilters }: FiltersProps) => {
  return (
    <Box sx={{ display: 'flex', gap: 2, paddingBottom: 4 }}>
      <EventTypeFilter
        value={filters.eventType}
        onChange={value => setFilters({ ...filters, eventType: value as MaDealType })}
      />
      <CountryFilter
        value={filters.country}
        onChange={value => setFilters({ ...filters, country: value as Country })}
      />
      <BuyerFilter
        value={filters.buyer}
        onChange={value => setFilters({ ...filters, buyer: value as Company })}
      />
      <SellerFilter
        value={filters.seller}
        onChange={value => setFilters({ ...filters, seller: value as Company })}
      />
      <AssetFilter
        value={filters.asset}
        onChange={value => setFilters({ ...filters, asset: value as Asset })}
      />
    </Box>
  );
};

export const MaDealsList = () => {
  const TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
  const [filters, setFilters] = useState<MaDealsListFilters>({
    eventType: null,
    country: null,
    buyer: null,
    seller: null,
    asset: null,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'effectiveDate', sort: 'desc' },
  ]);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: TABLE_PAGE_SIZE_OPTIONS[0],
  });

  const { deals, loading, error, totalCount } = useGetDeals({
    filters: {
      eventTypes: filters.eventType ? [filters?.eventType?.id] : [],
      countries: filters.country ? [filters?.country?.isoCode] : [],
      buyers: filters.buyer ? [Number(filters?.buyer?.id)] : [],
      sellers: filters.seller ? [Number(filters?.seller?.id)] : [],
      assets: filters.asset ? [Number(filters?.asset?.id)] : [],
    },
    page: paginationModel.page,
    perPage: paginationModel.pageSize,
    order: sortModel[0]?.sort,
    orderBy: sortModel[0]?.field,
  });

  // Memoize the rowCount to prevent the table from re-setting to 0 when the totalCount changes
  // Reference: https://mui.com/x/react-data-grid/pagination/
  const rowCountRef = useRef(totalCount || 0);
  const rowCount = useMemo(() => {
    if (totalCount !== undefined) {
      rowCountRef.current = totalCount;
    }
    return rowCountRef.current;
  }, [totalCount]);

  if (error) return <div>Error loading MA Deals</div>;

  return (
    <AdminsPageShell title='M&A Deals'>
      <Filters filters={filters} setFilters={setFilters} />
      {/* Deals Table */}
      <DataGrid
        loading={loading}
        rows={deals}
        rowCount={rowCount}
        columns={TABLE_COLUMNS}
        sortingMode='server'
        paginationMode='server'
        pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        disableRowSelectionOnClick
      />
    </AdminsPageShell>
  );
};
